import type { Program } from '@compass/client/types/program';
import type { Team } from '@compass/client/types/team';
import { useStore } from '@store/index';
import { useEffect, useMemo, useRef } from 'react';

import {
  useWorkforceAccessMode,
  useWorkforceEligiblePrograms,
} from '@/hooks/useShouldShowWorkforceConsole';
import type { SelectOption } from '@/types/formSchema';

import { useMyManagedTeams } from './useMyManagedTeams';
import { MAX_PROGRAM_FANOUT, type ProgramCasesTuple } from './useProgramCases';
import type { OrgProgramPair } from './useWorkflowSlaMap';
import { useWorkforceTeams } from './useWorkforceTeams';

export interface TeamProgramMatch {
  team: Team;
  program: Program;
}

// Stable per-module singletons — see useWorkforceCases for the rationale on
// avoiding `Object.freeze` here.
const EMPTY_TUPLES: ProgramCasesTuple[] = [];
const EMPTY_PAIRS: OrgProgramPair[] = [];

/**
 * Resolve each selected team to ALL programs it belongs to (a program lists the
 * team in its `teams[]`). A team may belong to many programs, so the output is
 * flattened to one `{ team, program }` pair per match, in team-outer /
 * program-inner order. Teams without an id and programs without an id are
 * dropped.
 *
 * Pure function — exported for unit tests.
 */
export function resolveTeamPrograms(
  selectedTeams: Team[],
  programs: Program[],
): TeamProgramMatch[] {
  const out: TeamProgramMatch[] = [];
  for (const team of selectedTeams) {
    const teamId = team.id;
    if (!teamId) continue;
    for (const program of programs) {
      if (!program.id) continue;
      if (program.teams?.includes(teamId)) out.push({ team, program });
    }
  }
  return out;
}

/**
 * Collapse team→program matches into one fetch tuple per program, whose
 * `assignedUserIds` is the union of `member_ids + manager_id` across all
 * matched teams serving that program. This is what the assignee-based fetch
 * (`useProgramCases`) consumes: cases in program P owned by any member (or
 * lead) of the selected teams — regardless of the case's own team.
 *
 * Programs whose assignee union is empty are dropped: an empty
 * `assigned_user_ids` means "unassigned" server-side, which we must never send.
 * `manager_id` is always present in practice, so the guard is defensive.
 *
 * Pure function — exported for unit tests.
 */
export function buildProgramCasesTuples(
  orgId: string,
  matches: TeamProgramMatch[],
): ProgramCasesTuple[] {
  const byProgram = new Map<string, Set<string>>();
  for (const { team, program } of matches) {
    const pid = program.id;
    if (!pid) continue;
    let set = byProgram.get(pid);
    if (!set) {
      set = new Set<string>();
      byProgram.set(pid, set);
    }
    if (team.manager_id) set.add(team.manager_id);
    for (const uid of team.member_ids ?? []) set.add(uid);
  }
  const out: ProgramCasesTuple[] = [];
  for (const [programId, ids] of byProgram) {
    if (ids.size === 0) continue;
    out.push({ orgId, programId, assignedUserIds: [...ids].sort() });
  }
  return out;
}

/**
 * The default team selection. All eligible teams when the programs they serve
 * (counted as the case fetch counts them) fit under `MAX_PROGRAM_FANOUT`.
 * Otherwise the teams serving `selectedProgramId`, or every team when none
 * of those fits, picked greedily in switcher order: a team is skipped when it would
 * push the picked teams' programs over the cap.
 *
 * Pure function — exported for unit tests.
 */
export function pickDefaultTeamIds(
  orgId: string,
  eligibleTeams: Team[],
  programs: Program[],
  selectedProgramId: string | null,
): string[] {
  const teams = eligibleTeams.filter((t): t is Team & { id: string } => !!t.id);
  const fits = (candidate: Team[]) =>
    buildProgramCasesTuples(orgId, resolveTeamPrograms(candidate, programs)).length <=
    MAX_PROGRAM_FANOUT;
  if (fits(teams)) return teams.map(t => t.id);

  const selectedProgram = programs.find(p => p.id && p.id === selectedProgramId);
  const serving = teams.filter(t => selectedProgram?.teams?.includes(t.id));
  const greedy = (candidates: Array<Team & { id: string }>) => {
    const picked: Array<Team & { id: string }> = [];
    for (const team of candidates) {
      if (fits([...picked, team])) picked.push(team);
    }
    return picked.map(t => t.id);
  };
  const servingPick = greedy(serving);
  return servingPick.length > 0 ? servingPick : greedy(teams);
}

/**
 * The teams the switcher lists and the programs they are resolved against, for
 * the console's access mode (`useWorkforceAccessMode`). Only the active mode's
 * requests are sent.
 *
 * - Team-lead mode (`permissions_based_wc_access` off or absent): the teams the
 *   user leads in the selected org (`useMyManagedTeams`), resolved against every
 *   program of the org, exactly as before BV-2303.
 * - Permission-based mode: every team linked to a program in which the user may
 *   use the console (`useWorkforceEligiblePrograms`: the permission map grants
 *   the console's APIs there), resolved against those programs only. Org-level
 *   and global admins are eligible in every program, so they see every team
 *   linked to the org's programs. Team leadership plays no part.
 */
function useWorkforceTeamSource(
  organizationId: string | null,
  permissionsBased: boolean,
): { teams: Team[]; programs: Program[]; isLoading: boolean } {
  const getProgramsArray = useStore(state => state.getProgramsArray);
  const orgPrograms = useMemo(() => getProgramsArray(), [getProgramsArray]);
  const leads = useMyManagedTeams({ enabled: !permissionsBased });

  const eligible = useWorkforceEligiblePrograms(permissionsBased);
  const linked = useWorkforceTeams(organizationId, eligible.programs);

  if (!permissionsBased) {
    return { teams: leads.managedTeams, programs: orgPrograms, isLoading: leads.isLoading };
  }
  return {
    teams: linked.teams,
    programs: eligible.programs,
    isLoading: eligible.isLoading || linked.isLoading,
  };
}

/**
 * Owns all filter-state derivation for the Workforce Console: eligible teams,
 * selected teams/programs, team→program resolution, fan-out tuples, and the
 * option lists rendered by the filter row. Self-contained side-effects keep
 * the persisted selections coherent across org switches.
 *
 * Which teams are eligible depends on the access mode; see
 * `useWorkforceTeamSource`.
 */
export function useWorkforceSelection() {
  const {
    selectedOrganizationId,
    selectedProgramId,
    workforceSelectedTeamIds,
    workforceSelectedProgramIds,
    setWorkforceSelectedTeamIds,
    setWorkforceSelectedProgramIds,
  } = useStore();

  const { permissionsBased } = useWorkforceAccessMode();
  const {
    teams: eligibleTeams,
    programs,
    isLoading: teamsLoading,
  } = useWorkforceTeamSource(selectedOrganizationId, permissionsBased);

  const programNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const program of programs) {
      if (program.id) map.set(program.id, program.name ?? program.id);
    }
    return map;
  }, [programs]);

  // Once the eligible-teams list has fully loaded (per org), apply the default
  // selection. The ref is keyed on `selectedOrganizationId` so switching orgs
  // re-arms the default for the new org's teams. Clearing the selection
  // afterwards is respected: the grid then shows "Select a team to get started".
  //
  // Team-lead mode keeps its old default: every led team, only when nothing is
  // saved. Permission-based mode applies `pickDefaultTeamIds` unless the
  // persisted selection still names an eligible team. Waiting for the load to
  // finish keeps a program whose teams arrive last from being left out of the
  // default. The persisted selection is not cleared on an org switch, so ids
  // from another org (or teams the user lost access to) count as no selection.
  const lastAutoSelectedOrgRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedOrganizationId) return;
    if (lastAutoSelectedOrgRef.current === selectedOrganizationId) return;
    if (teamsLoading || eligibleTeams.length === 0) return;
    lastAutoSelectedOrgRef.current = selectedOrganizationId;
    const eligibleIds = eligibleTeams
      .filter((t): t is Team & { id: string } => !!t.id)
      .map(t => t.id);
    if (!permissionsBased) {
      if (workforceSelectedTeamIds.length === 0) setWorkforceSelectedTeamIds(eligibleIds);
    } else if (!eligibleIds.some(id => workforceSelectedTeamIds.includes(id))) {
      setWorkforceSelectedTeamIds(
        pickDefaultTeamIds(selectedOrganizationId, eligibleTeams, programs, selectedProgramId),
      );
    }
  }, [
    selectedOrganizationId,
    selectedProgramId,
    permissionsBased,
    teamsLoading,
    eligibleTeams,
    programs,
    workforceSelectedTeamIds,
    setWorkforceSelectedTeamIds,
  ]);

  // Defensively filter persisted team IDs against the live eligible-teams list.
  const selectedTeams = useMemo(
    () => eligibleTeams.filter(t => t.id && workforceSelectedTeamIds.includes(t.id)),
    [eligibleTeams, workforceSelectedTeamIds],
  );
  // What the team switcher shows as checked. Permission-based mode counts only
  // eligible ids, so a persisted id that is no longer eligible (another org's
  // team, a lost grant) is not counted; team-lead mode shows the persisted ids
  // as before.
  const selectedTeamIds = useMemo(
    () =>
      permissionsBased
        ? selectedTeams.flatMap(t => (t.id ? [t.id] : []))
        : workforceSelectedTeamIds,
    [permissionsBased, selectedTeams, workforceSelectedTeamIds],
  );
  // The teams whose (program → workflow-type) reach scopes the fetched cases:
  // the selected teams in permission-based mode, so the table reflects exactly
  // the selection; every led team in team-lead mode, as before.
  const scopeTeams = permissionsBased ? selectedTeams : eligibleTeams;

  const teamProgramMatches = useMemo(
    () => resolveTeamPrograms(selectedTeams, programs),
    [selectedTeams, programs],
  );

  // Fan out one (org, program) tuple per program the selected teams serve — the
  // FULL union, NOT narrowed by the program selector. Each tuple carries the
  // union of its serving teams' members as the `assigned_user_ids` filter, so a
  // case owned by any selected team's member shows even if its own team_id is
  // foreign. The program *filter* is applied client-side downstream, so toggling
  // a program never refetches.
  const programCasesTuples: ProgramCasesTuple[] = useMemo(
    () =>
      selectedOrganizationId
        ? buildProgramCasesTuples(selectedOrganizationId, teamProgramMatches)
        : EMPTY_TUPLES,
    [teamProgramMatches, selectedOrganizationId],
  );

  // SLA/workflow data must cover EVERY program a visible case may belong to, so
  // derive the (org, program) pairs from the full union of team→program matches
  // rather than from the program-filtered subset (the program filter is applied
  // client-side, downstream of the fetch).
  const slaPairs: OrgProgramPair[] = useMemo(
    () =>
      selectedOrganizationId
        ? teamProgramMatches.map(m => ({
            orgId: selectedOrganizationId,
            programId: m.program.id ?? '',
          }))
        : EMPTY_PAIRS,
    [teamProgramMatches, selectedOrganizationId],
  );

  const programOptions = useMemo<SelectOption[]>(() => {
    const seen = new Set<string>();
    const options: SelectOption[] = [];
    for (const { program } of teamProgramMatches) {
      const id = program.id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      options.push({ value: id, label: program.name ?? id });
    }
    return options;
  }, [teamProgramMatches]);

  // Drop stale program selections after a team de-select wipes their last team.
  useEffect(() => {
    if (workforceSelectedProgramIds.length === 0) return;
    const valid = new Set(programOptions.map(opt => String(opt.value)));
    const filtered = workforceSelectedProgramIds.filter(id => valid.has(id));
    if (filtered.length !== workforceSelectedProgramIds.length) {
      setWorkforceSelectedProgramIds(filtered);
    }
  }, [workforceSelectedProgramIds, programOptions, setWorkforceSelectedProgramIds]);

  const teamSelectOptions = useMemo(
    () =>
      eligibleTeams
        .filter((t): t is Team & { id: string } => !!t.id)
        .map(t => ({ value: t.id, label: t.name ?? t.id })),
    [eligibleTeams],
  );

  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of eligibleTeams) {
      if (t.id) map.set(t.id, t.name ?? t.id);
    }
    return map;
  }, [eligibleTeams]);

  return {
    selectedOrganizationId,
    /** Every team the user can pick in the team switcher (superset of
     *  `selectedTeams`). Used by reassign to resolve a valid target team. */
    eligibleTeams,
    /** The programs teams are resolved against (with their `teams[]` linkage):
     *  where the user may use the console in permission-based mode, every org
     *  program in team-lead mode. Used by reassign to validate a target team
     *  against a case's program. */
    programs,
    selectedTeams,
    /** Ids the team switcher shows as checked. */
    selectedTeamIds,
    /** Teams that scope fetched cases to their workflow-type reach
     *  (`useWorkforceCases`). */
    scopeTeams,
    teamProgramMatches,
    programOptions,
    programNameById,
    teamSelectOptions,
    teamNameById,
    slaPairs,
    programCasesTuples,
    workforceSelectedTeamIds,
    workforceSelectedProgramIds,
    setWorkforceSelectedTeamIds,
    setWorkforceSelectedProgramIds,
    teamsLoading,
  };
}
